// client/src/v2/domains/home/useHomeLocation.js
import { useCallback, useState } from "react";
import { resolveHomeLocation } from "./home.service";
import { reverseGeocodeLatLon } from "./home.api";
import { isValidUkPostcode, normalizeUkPostcode } from "./ukPostcode";
import { UI_MSG } from "../../config/uiMessages";

// SW19 5AB, Wimbledon. Same point Nominatim returns for that postcode.
const DEFAULT_HOME_POSITION = [51.42936, -0.21234];

export function useHomeLocation(initialPostcode) {
  const [postcode, setPostcode] = useState(initialPostcode);
  const [homePosition, setHomePosition] = useState(DEFAULT_HOME_POSITION);
  const [homeError, setHomeError] = useState(null);

  const clearHomeError = useCallback(() => {
    setHomeError(null);
  }, []);

  const submitPostcode = useCallback(async (raw) => {
    const trimmed = normalizeUkPostcode(raw);

    if (!isValidUkPostcode(trimmed)) {
      setHomeError(UI_MSG.POSTCODE_FORMAT);
      return false;
    }

    try {
      setHomeError(null);
      setPostcode(trimmed);

      const { lat, lon } = await resolveHomeLocation(trimmed);
      setHomePosition([lat, lon]);
      return true;
    } catch (err) {
      setHomeError(err?.message || UI_MSG.POSTCODE_LOOKUP_UNAVAILABLE);
      return false;
    }
  }, []);

  /** Map / locate: keep pin immediately; fill top-bar postcode via reverse geocode. */
  const setHomeFromMap = useCallback((lat, lon) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    setHomeError(null);
    setHomePosition([lat, lon]);

    reverseGeocodeLatLon(lat, lon)
      .then(({ postcode: found }) => {
        if (found) setPostcode(normalizeUkPostcode(found));
      })
      .catch(() => {
        // Keep pin; leave postcode as-is if reverse fails (don't blank the bar).
      });
  }, []);

  return {
    postcode,
    setPostcode,
    homePosition,
    homeError,
    clearHomeError,
    submitPostcode,
    setHomeFromMap,
  };
}
